import {Inject, Injectable} from '@angular/core';
import {EMPTY, Observable} from 'rxjs';
import {catchError, map, timeout} from 'rxjs/operators';
import {MnetUserResource, UserResource} from '@services/resource/user-resource';
import {HttpClient, HttpParams} from '@angular/common/http';
import {ConfigService} from '@services/infra/config.service';
import {User, UserSearchResult, UserSearchResultResource} from '@services/model/user';
import {ClientConfig} from '@app/client-config';
import {ErrorService} from '@services/error.service';
import {LoggingService} from '@services/logging.service';
import {removeBlanks} from '@util/object';
import {UUID} from '@util/types';
import {SESSION_STORAGE, StorageService} from 'ngx-webstorage-service';
import {User as ToolKitUser, UserService} from '@common/messe-ui-toolkit';
import {Router} from '@angular/router';
import {RouteBuilder} from '@util/route-builder';

const USERKEY = 'currentUser';
const TOOLKITUSERKEY = 'currentToolkitUser';

type Permission = 'vote';

export class SearchUserParams {
  q?: string;
  offset: string;
  limit: string;
  filterUserType?: string;
  filterUserPrivilege?: string;

  constructor(q: string | undefined, offset: number, limit: number, filterUserType: string | undefined, filterUserPrivilege: string | undefined) {
    this.q = q;
    this.offset = String(offset);
    this.limit = String(limit);
    this.filterUserType = filterUserType;
    this.filterUserPrivilege = filterUserPrivilege;
  }
}

@Injectable({
  providedIn: 'root',
})
export class VoilaUserService extends UserService {
  constructor(
    private readonly router: Router,
    private readonly errors: ErrorService,
    private readonly http: HttpClient,
    private readonly conf: ConfigService,
    private readonly logging: LoggingService,
    @Inject(SESSION_STORAGE) private readonly storage: StorageService,
  ) {
    super();
  }

  public writeUserToStorage(user: User) {
    this.storage.set(USERKEY, user);
  }

  public read(mnetId: string, includeStartup: boolean): Observable<User> {
    const requestUrl = `${this.conf.backendUrl}/v1/user?mnetId=${mnetId}&includeStartup=${includeStartup}`;
    return this.http.get<UserResource>(requestUrl, {withCredentials: true}).pipe(
      timeout(ClientConfig.timeout),
      this.errors.withErrorHandler(),
      map((u) => User.fromResource(u)),
      catchError(() => {
        this.router.navigate([RouteBuilder.error().build()]);
        return EMPTY;
      }),
    );
  }

  public saveUser(userId: UUID, user: User): Observable<void> {
    const url = `${this.conf.backendUrl}/v1/user/${userId}`;
    return this.http.put<void>(url, User.toResource(user));
  }

  public readFromMnet(mnetId: string): Observable<MnetUserResource> {
    const url = `${this.conf.backendUrl}/v1/user-management/user/${mnetId}`;
    return this.http.post<MnetUserResource>(url, null).pipe(timeout(ClientConfig.timeout), this.errors.withErrorHandler());
  }

  public searchUser(q: SearchUserParams): Observable<UserSearchResult> {
    const url = `${this.conf.backendUrl}/v1/user/search`;
    const params = new HttpParams({fromObject: removeBlanks(q) as any});
    return this.http.get<UserSearchResultResource>(url, {params}).pipe(map(UserSearchResult.fromResource));
  }

  public update(id: string, userResource: UserResource): Observable<UserResource> {
    const requestUrl = `${this.conf.backendUrl}/v1/user/${id}`;
    return this.http.put<UserResource>(requestUrl, userResource, {withCredentials: true}).pipe(timeout(ClientConfig.timeout), this.errors.withErrorHandler());
  }

  public givePermission(userId: string, permission: Permission): Observable<void> {
    const url = `${this.conf.backendUrl}/v1/user/${userId}/permissions/${permission}`;
    return this.http.put<void>(url, {});
  }

  public removePermission(userId: string, permission: Permission): Observable<void> {
    const url = `${this.conf.backendUrl}/v1/user/${userId}/permissions/${permission}`;
    return this.http.delete<void>(url);
  }

  currentUser(): User {
    return this.storage.get(USERKEY);
  }

  loadUserData(mnetId: string): Observable<void> {
    return this.read(mnetId, true).pipe(
      map((user) => {
        this.logging.logInfo('user successfully logged in', {username: mnetId});
        const toolKitUser = new ToolKitUser(user.firstName, user.lastName, user.companyId, user.mnetId, user.userType, [], []);
        this.storage.set(TOOLKITUSERKEY, toolKitUser);
        this.writeUserToStorage(user);
        this.currentUser$.next(toolKitUser);
      }),
    );
  }
}
